import { getUserIdCookie, getRegistrationDateCookie } from "@/utils";

export interface Creds {
  userId: string;
  registrationDate: string;
}

export type CredsEvent =
  | { type: "appeared"; creds: Creds }
  | { type: "changed"; creds: Creds; previous: Creds }
  | { type: "removed"; previous: Creds };

const COOKIE_POLL_INTERVAL_MS = 2000;
const DEBOUNCE_MS = 250;
const WATCHED_ATTRS = ["data-user-id", "data-registration-date"] as const;

function readCreds(): Creds | null {
  const userId = getUserIdCookie();
  const registrationDate = getRegistrationDateCookie();
  if (!userId || !registrationDate) return null;
  return { userId, registrationDate };
}

function tupleKey(c: Creds | null): string {
  return c ? `${c.userId}::${c.registrationDate}` : "";
}

export function startCredentialsWatcher(
  onEvent: (e: CredsEvent) => void
): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return () => {};
  }

  let current: Creds | null = null;
  let lastKey = "";
  let debounceHandle: ReturnType<typeof setTimeout> | null = null;
  let isActive = true;

  const evaluate = (): void => {
    if (!isActive) return;
    const next = readCreds();
    const nextKey = tupleKey(next);
    if (nextKey === lastKey) return;

    const previous = current;
    current = next;
    lastKey = nextKey;

    if (!previous && next) {
      onEvent({ type: "appeared", creds: next });
    } else if (previous && next) {
      onEvent({ type: "changed", creds: next, previous });
    } else if (previous && !next) {
      onEvent({ type: "removed", previous });
    }
  };

  const scheduleEvaluate = (): void => {
    if (!isActive) return;
    if (debounceHandle !== null) clearTimeout(debounceHandle);
    debounceHandle = setTimeout(() => {
      debounceHandle = null;
      evaluate();
    }, DEBOUNCE_MS);
  };

  evaluate();

  const observer = new MutationObserver(() => {
    scheduleEvaluate();
  });
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: [...WATCHED_ATTRS],
  });

  const pollHandle = setInterval(scheduleEvaluate, COOKIE_POLL_INTERVAL_MS);

  return () => {
    isActive = false;
    observer.disconnect();
    clearInterval(pollHandle);
    if (debounceHandle !== null) {
      clearTimeout(debounceHandle);
      debounceHandle = null;
    }
  };
}
